import type { AxiosResponse } from 'axios';

/**
 * Trigger a browser file download from blob/binary data.
 */
export function downloadBlobAsFile(
  data: BlobPart | Blob,
  filename: string,
  mimeType?: string
): void {
  const blob =
    data instanceof Blob
      ? data
      : new Blob([data], mimeType ? { type: mimeType } : undefined);

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Chrome needs the Object URL to remain available while the download starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Trigger a browser download from an axios response containing a Blob.
 *
 * Reads the filename from the `Content-Disposition` header when present,
 * otherwise falls back to `fallbackFilename`. Backends should always set
 * `Content-Disposition: attachment; filename="..."` so the user gets a
 * meaningful filename.
 */
export function downloadResponseAsFile<T>(
  response: AxiosResponse<T>,
  fallbackFilename: string
): void {
  const disposition = response.headers?.['content-disposition'];
  const match =
    typeof disposition === 'string'
      ? disposition.match(/filename="?([^";]+)"?/)
      : null;
  const filename = match?.[1] || fallbackFilename;

  downloadBlobAsFile(response.data as unknown as BlobPart | Blob, filename);
}
